<template>
  <div class="protocol">
    <input-dropdown
      v-model:value="selectedStack"
      :options="['model', 'simulation', 'evaluation']"
    />

    <drawer :nested="true" :title="$t('labels.undoable')">
      <ul class="protocol__list protocol_past">
        <li
          v-for="group in pastByGroup"
          :key="group.id"
          class="protocol__entry"
        >
          <div>
            <undo-protocol-entry v-for="undo in group.undos" :key="undo.id" :undo="undo" />
          </div>
        </li>
      </ul>
    </drawer>

    <drawer :nested="true" :title="$t('labels.redoable')">
      <ul class="protocol__list protocol_future">
        <li
          v-for="group in futureByGroup"
          :key="group.id"
          class="protocol__entry"
        >
          <div>
            <undo-protocol-entry v-for="undo in group.undos" :key="undo.id" :undo="undo" />
          </div>
        </li>
      </ul>
    </drawer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useUndo } from "@editor/store/undo/undo.store";

import InputDropdown from "@core/input/InputDropdown.vue";
import Drawer from "@core/ui/DrawerBlock.vue";
import UndoProtocolEntry from "@editor/components/UndoProtocolEntry.vue";
import type { UndoEntry } from "@editor/store/undo/undo.store";

const selectedStack = ref<"model" | "simulation" | "evaluation">("model");
const stack = ref({
  past: {} as { [key: string]: UndoEntry },
  future: [] as UndoEntry[]
});

const past = computed((): UndoEntry[] => {
  return Object.values(stack.value.past ?? {})
    .filter((item) => item != null)
    .sort((a, b) => a.id - b.id);
});
const future = computed(() => {
  return stack.value.future
    .slice(0)
    .filter((item) => item != null)
    .sort((a, b) => a.id - b.id);
});
const pastByGroup = computed(() => {
  let order = 0;
  return Object.values(past.value.reduce((groups, undo) => {
    if (!groups[undo.group]) groups[undo.group] = {
      undos: [],
      order: order++,
      id: undo.group
    };
    groups[undo.group].undos.unshift(undo);
    return groups;
  }, {} as { [key: string]: { undos: UndoEntry[]; id: number; order: number } })).sort((a, b) => b.order - a.order);
});
const futureByGroup = computed(() => {
  return Object.values(future.value.reduce((groups, undo) => {
    if (!groups[undo.group]) groups[undo.group] = {
      undos: [],
      order: undo.id,
      id: undo.group
    };
    groups[undo.group].undos.unshift(undo);
    return groups;
  }, {} as { [key: string]: { undos: UndoEntry[]; id: number; order: number } })).sort((a, b) => a.order - b.order);
});

const undoPastLength = computed(() => {
  return useUndo().stackPastLengths[selectedStack.value];
});

function updateStack() {
  const undoStack = useUndo().getStack(selectedStack.value);
  if (undoStack) {
    stack.value = undoStack;
  }
}

watch(undoPastLength, () => {
  updateStack();
}, { immediate: true });
</script>

<style lang="scss">
.protocol {
  text-align: left;
  font-size: 12px;

  &__list {
    border-radius: 5px;
    padding-left: 1rem;
    padding-right: 1rem;
    list-style: none;
  }

  &_past {
    background-color: rgb(240, 240, 255);
  }
  &_future {
    background-color: rgb(255, 255, 230);
  }

  &__entry {
    padding: 0.5rem 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem;

    strong {
      text-align: right;
    }

    & + & {
      border-top: 1px solid var(--color-shadow-1);
    }
  }
}
</style>
